import React, { useCallback, useId, useState } from 'react';
import Modal from './Modal';
import { ModalActions } from './ModalFooter';
import { useLanguage } from '../../contexts/LanguageContext';

function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  cancelLabel,
  confirmVariant = 'danger',
  loading = false,
}) {
  const { t } = useLanguage();
  const messageId = useId();

  return (
    <Modal
      isOpen={isOpen}
      onClose={loading ? () => {} : onClose}
      title={title || t.common?.confirm || 'Confirm'}
      size="small"
      closeOnOverlayClick={!loading}
      aria-describedby={message ? messageId : undefined}
      footer={
        <ModalActions
          onCancel={loading ? undefined : onClose}
          onConfirm={onConfirm}
          cancelLabel={cancelLabel || t.common?.cancel || 'Cancel'}
          confirmLabel={confirmLabel || t.common?.confirm || 'Confirm'}
          confirmVariant={confirmVariant}
          loading={loading}
          initialFocus
        />
      }
    >
      {message ? (
        <p id={messageId} className="text-sm leading-relaxed text-secondary-600 ">
          {message}
        </p>
      ) : null}
    </Modal>
  );
}

/**
 * Promise-based confirm for replacing window.confirm.
 * Returns { confirm, dialog } — render `dialog` once near the view root.
 */
export function useConfirmDialog() {
  const { t } = useLanguage();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      setState({
        title: options.title,
        message: options.message,
        confirmLabel: options.confirmLabel,
        cancelLabel: options.cancelLabel,
        confirmVariant: options.confirmVariant || 'danger',
        resolve,
      });
    });
  }, []);

  const handleClose = useCallback(() => {
    if (loading) return;
    state?.resolve?.(false);
    setState(null);
  }, [loading, state]);

  const handleConfirm = useCallback(async () => {
    if (!state) return;
    setLoading(true);
    try {
      state.resolve(true);
      setState(null);
    } finally {
      setLoading(false);
    }
  }, [state]);

  const dialog = (
    <ConfirmDialog
      isOpen={Boolean(state)}
      onClose={handleClose}
      onConfirm={handleConfirm}
      title={state?.title}
      message={state?.message}
      confirmLabel={state?.confirmLabel || t.common?.delete}
      cancelLabel={state?.cancelLabel}
      confirmVariant={state?.confirmVariant}
      loading={loading}
    />
  );

  return { confirm, dialog };
}

export default ConfirmDialog;
